import isEmpty from 'lodash/isEmpty';

import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { API, get } from 'utils/api';
import { createReducer } from 'utils/redux';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';

const RECEIVE = 'brandCampaigns/RECEIVE';
const RECEIVE_CCM = 'brandCampaigns/RECEIVE_CCM';
const RECEIVE_TOTAL_COUNT = 'brandCampaigns/RECEIVE_TOTAL_COUNT';
const RECEIVE_TOTAL_COUNT_CCM = 'brandCampaigns/RECEIVE_TOTAL_COUNT_CCM';
const RECEIVE_OVERVIEWS = 'brandCampaigns/RECEIVE_OVERVIEWS';
const RESET = 'brandCampaigns/RESET';
const LOADING_STATE_CHANGED = 'brandCampaigns/LOADING_STATE_CHANGED';
const LOADING_STATE_CHANGED_CCM = 'brandCampaigns/LOADING_STATE_CHANGED_CCM';
const LOADING_STATE_COUNTS_CHANGED = 'brandCampaigns/LOADING_STATE_COUNTS_CHANGED';

import type {
  Action,
  Campaign,
  CampaignOverview,
  CampaignQuery,
  CampaignState,
  CampaignType,
  Pagination,
  ThunkAction
} from 'types';

const { CAMPAIGNS, BRANDS, SUBCAMPAIGNS } = API.END_POINTS;

const campaignStates: Array<CampaignState> = ['active', 'inactive', 'preparation'];

type TotalCount = {
  active: number,
  inactive: number,
  preparation: number
};

export type StateType = {
  totalCount: TotalCount,
  totalCountCcm: TotalCount,
  hasCampaigns: boolean,
  list: Array<Campaign> | null,
  listCcm: Array<Campaign> | null,
  pagination: Pagination,
  paginationCcm: Pagination,
  overviewsByCampaignId: {
    [x: string]: CampaignOverview
  },
  isLoadingCounts: boolean,
  isLoading: boolean,
  isLoadingCcm: boolean
};

const initialState: StateType = {
  totalCount: {
    active: 0,
    inactive: 0,
    preparation: 0
  },
  totalCountCcm: {
    active: 0,
    inactive: 0,
    preparation: 0
  },
  hasCampaigns: false,
  list: null,
  listCcm: null,
  pagination: {},
  paginationCcm: {},
  overviewsByCampaignId: {},
  isLoadingCounts: false,
  isLoading: false,
  isLoadingCcm: false
};

function loadingStateChanged(isLoading: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      isLoading
    }
  };
}

function loadingStateChangedCcm(isLoadingCcm: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED_CCM,
    payload: {
      isLoadingCcm
    }
  };
}

function loadingStateCountsChanged(isLoadingCounts: boolean): Action {
  return {
    type: LOADING_STATE_COUNTS_CHANGED,
    payload: {
      isLoadingCounts
    }
  };
}

function receiveCampaigns(data): Action {
  return {
    type: RECEIVE,
    payload: {
      data
    }
  };
}

function receiveCcmCampaigns(data): Action {
  return {
    type: RECEIVE_CCM,
    payload: {
      data
    }
  };
}

function receiveCampaignsCount(data: TotalCount, canReset: boolean = false): Action {
  return {
    type: RECEIVE_TOTAL_COUNT,
    payload: {
      data,
      canReset
    }
  };
}

function receiveCampaignsCountCcm(data: TotalCount, canReset: boolean = false): Action {
  return {
    type: RECEIVE_TOTAL_COUNT_CCM,
    payload: {
      data,
      canReset
    }
  };
}

function receiveOverviews(
  overviewsByCampaignId: {
    [x: number]: CampaignOverview
  }
): Action {
  return {
    type: RECEIVE_OVERVIEWS,
    payload: {
      overviewsByCampaignId
    }
  };
}

export function resetCampaigns(): Action {
  return {
    type: RESET
  };
}

function fetchCampaignsCountByState(
  organizationId: number,
  filter: string,
  campaignState: CampaignState,
  search: string,
  campaignTypes?: Array<CampaignType>
): Promise<number> {
  let updatedCampaignState = campaignState as string;

  if (campaignTypes?.includes('ccm')) {
    updatedCampaignState = campaignState === 'preparation'
      ? `draft,${campaignState}`
      : campaignState;
  }

  const params = snakeCaseKeys({
    campaignState: updatedCampaignState,
    filterBy: filter,
    search,
    page: 1,
    itemsPerPage: 1,
    ...(!isEmpty(campaignTypes)
      ? { campaignTypes } // if campaign type(s) are passed, use them
      : { campaignTypes: ['collab'] }) // otherwise, default to collab campaigns
  });

  const queryString = Querystring.objectToString(params);
  const url = `${BRANDS.getBrandCampaignsV1(organizationId)}?${queryString}`;

  return get(url).then(response => response && response.data.meta.itemsTotal);
}

export function fetchCampaignsCount(
  organizationId: number,
  filter: string,
  search: string,
  campaignTypes?: Array<CampaignType>
): ThunkAction {
  return dispatch => {
    dispatch(loadingStateCountsChanged(true));

    return Promise.all(campaignStates
      .map(state => fetchCampaignsCountByState(organizationId, filter, state, search, campaignTypes))
    )
      .then(results => results.reduce((data, result, index) => {
        // eslint-disable-next-line no-param-reassign
        data[campaignStates[index]] = results[index];
        return data;
      }, {} as TotalCount))
      .then(data => {
        if (campaignTypes?.includes('ccm')) {
          dispatch(receiveCampaignsCountCcm(data, search === ''));
        } else {
          dispatch(receiveCampaignsCount(data, search === ''));
        }

        dispatch(loadingStateCountsChanged(false));
        return data;
      });
  };
}

export function fetchBrandCampaigns(organizationId: number, query: CampaignQuery = {}): ThunkAction {
  const { filter, page, state, campaignTypes } = query;

  const params = snakeCaseKeys({
    campaignState: state || 'active',
    include: 'main_image',
    itemsPerPage: query.itemsPerPage || window.app.env.PAGINATION_LIMIT,
    filterBy: filter,
    search: query.search || '',
    page: page || 1,
    ...(!isEmpty(campaignTypes)
      ? { campaignTypes } // if campaign type(s) are passed, use them
      : { campaignTypes: ['collab'] }) // otherwise, default to collab campaigns
  });

  const queryString = Querystring.objectToString(params);
  const url = `${BRANDS.getBrandCampaignsV1(organizationId)}?${queryString}`;

  return (dispatch) => {
    if (campaignTypes?.includes('ccm')) {
      dispatch(loadingStateChangedCcm(true));
    } else {
      dispatch(loadingStateChanged(true));
    }

    return get(url)
      .then((response) => {
        const campaignsData = response.data;

        if (campaignTypes?.includes('ccm')) {
          dispatch(receiveCcmCampaigns(campaignsData));
        } else {
          dispatch(receiveCampaigns(campaignsData));
        }

        dispatch(loadingStateChanged(false));
        dispatch(loadingStateChangedCcm(false));
        return campaignsData;
      })
      .catch((error) => {
        logError(error);
        dispatch(loadingStateChanged(false));
        dispatch(loadingStateChangedCcm(false));
        return { data: [] };
      });
  };
}

export function fetchCampaignsOverviews(campaignIds: Array<number>): ThunkAction {
  const url = `${CAMPAIGNS.getCampaignsOverviewBulk()}?ids=${campaignIds.join(',')}`;
  const overviewsByCampaignId = {};

  return (dispatch) => {
    if (campaignIds.length === 0) {
      dispatch(receiveOverviews(overviewsByCampaignId));
      return Promise.resolve(overviewsByCampaignId);
    }

    return get(url)
      .then((response) => {
        const overviews = response.data;

        overviews.forEach((overview) => {
          overviewsByCampaignId[overview.id] = overview;
        });

        dispatch(receiveOverviews(overviewsByCampaignId));
        return overviewsByCampaignId;
      })
      .catch(() => {
        dispatch(receiveOverviews(overviewsByCampaignId));
        return overviewsByCampaignId;
      });
  };
}

export function fetchSubcampaignsByCampaignIds(campaignIds: Array<number>): ThunkAction {
  const url = SUBCAMPAIGNS.getSubcampaignsByCampaignBulk(campaignIds);
  const subcampaignsByCampaignId = {};

  return () => {
    if (campaignIds.length === 0) {
      return Promise.resolve(subcampaignsByCampaignId);
    }

    return get(url)
      .then((response) => {
        const campaignsSubcampaigns = response.data;

        campaignsSubcampaigns.forEach((subcampaign) => {
          const list = subcampaignsByCampaignId[subcampaign.campaignId] || [];
          subcampaignsByCampaignId[subcampaign.campaignId] = [
            ...list,
            subcampaign
          ];
        });

        return subcampaignsByCampaignId;
      })
      .catch((error) => {
        logError(error);
        return subcampaignsByCampaignId;
      });
  };
}

export default createReducer<StateType, any>('brandCampaigns', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { data }) => ({
    ...state,
    list: data.data,
    pagination: {
      ...data.meta,
      links: data.links
    }
  }),
  [RECEIVE_CCM]: (state, { data }) => ({
    ...state,
    listCcm: data.data,
    paginationCcm: {
      ...data.meta,
      links: data.links
    }
  }),
  [RECEIVE_TOTAL_COUNT]: (state, { data, canReset }) => ({
    ...state,
    totalCount: {
      ...state.totalCount,
      ...data
    },
    hasCampaigns: (canReset)
      ? Object.keys(data).some(key => data[key] > 0)
      : state.hasCampaigns
  }),
  [RECEIVE_TOTAL_COUNT_CCM]: (state, { data, canReset }) => ({
    ...state,
    totalCountCcm: {
      ...state.totalCountCcm,
      ...data
    },
    hasCampaigns: (canReset)
      ? Object.keys(data).some(key => data[key] > 0)
      : state.hasCampaigns
  }),
  [RECEIVE_OVERVIEWS]: (state, { overviewsByCampaignId }) => ({
    ...state,
    overviewsByCampaignId: {
      ...state.overviewsByCampaignId,
      ...overviewsByCampaignId
    }
  }),
  [LOADING_STATE_CHANGED]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [LOADING_STATE_CHANGED_CCM]: (state, { isLoadingCcm }) => ({
    ...state,
    isLoadingCcm
  }),
  [LOADING_STATE_COUNTS_CHANGED]: (state, { isLoadingCounts }) => ({
    ...state,
    isLoadingCounts
  })
});
